import type { AgentProcessStep, BlockAgentProcess } from "@do/shared-types";
import { ShimmeringText } from "@do/ui/components/animate-ui/primitives/texts/shimmering";
import { cn } from "@do/ui/lib/utils";
import { BotIcon, ChevronRightIcon, icons } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRef, useState } from "react";
import { useMotionEnabled } from "../../../hooks/use-motion-enabled";
import { INSTANT_TRANSITION } from "../../../lib/motion-presets";

type MessageAgentProcessProps = {
  block: BlockAgentProcess;
  id: string;
  /** Reports the `nodeId` of the hovered step, `null` when the pointer leaves it. */
  onStepHover?: (nodeId: string | null) => void;
};

const TRANSITION = { duration: 0.25, ease: "easeOut" } as const;

/**
 * Tailwind bakes its palette straight into the utilities and never exposes `--color-neutral-*` at
 * runtime, so the shimmer, which reads its colours from an inline style, has to carry the values
 * the same way the generated utilities do.
 */
const SHIMMER_TITLE = {
  color: "var(--color-neutral-500, #737373)",
  shimmeringColor: "var(--color-neutral-300, #d4d4d4)",
};

const SHIMMER_STEP = {
  color: "var(--color-neutral-800, #262626)",
  shimmeringColor: "var(--color-neutral-400, #a3a3a3)",
};

const STATUS_DOT_CLASSNAME: Record<AgentProcessStep["status"], string> = {
  error: "bg-red-500",
  running: "bg-neutral-300",
  success: "bg-green-500",
};

const getStepIcon = (step: AgentProcessStep) => {
  const icon = step.icon ? icons[step.icon as keyof typeof icons] : undefined;
  return icon ?? BotIcon;
};

/**
 * A step left in `running` once the run is over never got its closing event: it keeps its neutral
 * look but stops pulsing, so a finished trace never appears stuck.
 */
const isStepPending = (step: AgentProcessStep, isLive: boolean) =>
  isLive && step.status === "running";

const StepIcon = ({ isLive, step }: { isLive: boolean; step: AgentProcessStep }) => {
  const Icon = getStepIcon(step);

  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-sm transition-colors",
        step.iconClassName || "bg-neutral-100 text-neutral-700",
        step.status === "error" && "bg-red-50 text-red-600",
        isStepPending(step, isLive) && "animate-pulse"
      )}
    >
      <Icon className="size-4" />
    </span>
  );
};

/**
 * Timeline of what an agent did to build its answer. Collapsed to a chain of icons, it expands into
 * the detail of each reasoning step and tool call. Used by the agent testing tooling only.
 */
export const MessageAgentProcess = ({ block, id, onStepHover }: MessageAgentProcessProps) => {
  // `motionEnabled` is the one switch for every animation of the chat, this panel included.
  const transition = useMotionEnabled() ? TRANSITION : INSTANT_TRANSITION;
  const steps = block.steps ?? [];
  // Unfolded while the agent works, folded back once done, until the reader decides otherwise.
  const [expandedByUser, setExpandedByUser] = useState<boolean | null>(null);
  const hasToggledRef = useRef(false);

  const isLive = Boolean(block.isRunning);
  const isExpanded = hasToggledRef.current ? Boolean(expandedByUser) : isLive;
  const labels = block.labels ?? {};
  const title = labels.title ?? "Process";

  const hoverHandlers = (step: AgentProcessStep) =>
    onStepHover
      ? {
          onMouseEnter: () => onStepHover(step.nodeId ?? null),
          onMouseLeave: () => onStepHover(null),
        }
      : undefined;

  // A run that produced no event yet only announces itself; one that ended without any leaves
  // nothing behind.
  if (steps.length === 0) {
    return isLive ? (
      <motion.p
        animate={{ opacity: 1 }}
        className="text-sm"
        id={id}
        initial={{ opacity: 0 }}
        transition={transition}
      >
        <ShimmeringText text={`${title}…`} {...SHIMMER_TITLE} />
      </motion.p>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-3 -mb-2 text-sm text-neutral-600" id={id}>
      <button
        aria-controls={`${id}-steps`}
        aria-expanded={isExpanded}
        className="flex w-fit items-center gap-2 text-left"
        type="button"
        onClick={() => {
          hasToggledRef.current = true;
          setExpandedByUser(!isExpanded);
        }}
      >
        {isLive ? (
          <ShimmeringText text={title} {...SHIMMER_TITLE} />
        ) : (
          <span className="text-neutral-500">{title}</span>
        )}

        <span className="flex items-center gap-1">
          <AnimatePresence>
            {steps.map((step, index) => (
              <motion.span
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-1"
                initial={{ opacity: 0, scale: 0.6 }}
                key={step.id}
                transition={transition}
                {...hoverHandlers(step)}
              >
                {index > 0 && <span className="text-neutral-300">→</span>}
                <StepIcon isLive={isLive} step={step} />
              </motion.span>
            ))}
          </AnimatePresence>
        </span>

        <motion.span
          animate={{ rotate: isExpanded ? 90 : 0 }}
          className="flex"
          transition={transition}
        >
          <ChevronRightIcon className="size-4 text-neutral-400" />
        </motion.span>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            // A live run keeps growing: pinning its height would clip the steps still to come, so
            // only a folding triggered by the reader animates it.
            animate={isLive ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            className={cn(!isLive && "overflow-hidden")}
            exit={{ height: 0, opacity: 0, overflow: "hidden" }}
            initial={isLive ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={transition}
          >
            <ol className="relative flex flex-col gap-4" id={`${id}-steps`}>
              <span
                aria-hidden="true"
                className="absolute bottom-3 left-[4.5px] top-3 w-px bg-neutral-200"
              />

              <AnimatePresence>
                {steps.map((step) => (
                  <motion.li
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, y: -6 }}
                    key={step.id}
                    transition={transition}
                    {...hoverHandlers(step)}
                  >
                    <span
                      className={cn(
                        "z-1 mt-2 size-2.5 shrink-0 rounded-full transition-colors",
                        STATUS_DOT_CLASSNAME[step.status],
                        isStepPending(step, isLive) && "animate-pulse"
                      )}
                    />

                    <StepIcon isLive={isLive} step={step} />

                    <div className="flex min-w-0 flex-col gap-0.5">
                      <p className="font-medium text-neutral-800">
                        {isLive ? (
                          <ShimmeringText text={step.title} {...SHIMMER_STEP} />
                        ) : (
                          step.title
                        )}
                      </p>

                      {step.text && <p className="whitespace-pre-wrap">{step.text}</p>}

                      {step.parameters.length > 0 && (
                        <p className="break-words">
                          <span className="font-medium">{labels.parameters ?? "Arguments"} : </span>
                          {step.parameters
                            .map((parameter) => `${parameter.name}=${parameter.value}`)
                            .join(", ")}
                        </p>
                      )}

                      {/* Lands after the step itself, once the tool has answered. */}
                      {step.result && (
                        <motion.p
                          animate={{ opacity: 1 }}
                          className="break-words"
                          initial={{ opacity: 0 }}
                          transition={transition}
                        >
                          <span className="font-medium">{labels.result ?? "Return"} : </span>
                          {step.result}
                        </motion.p>
                      )}

                      {step.error && (
                        <motion.p
                          animate={{ opacity: 1 }}
                          className="break-words text-red-600"
                          initial={{ opacity: 0 }}
                          transition={transition}
                        >
                          {step.error}
                        </motion.p>
                      )}
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ol>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
